import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import {
  ArrowLeft, FileText, MapPin, Car, Calendar, ShieldCheck, Heart, AlertTriangle,
  Sparkles,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * TransferReadyView — 2-minute summary a new instructor can understand fast.
 * Rendered from /api/passport/transfer-view (learner's own) or via a shared token.
 */
export default function TransferReadyView() {
  const nav = useNavigate();
  const goBack = useSafeBack();
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/passport/transfer-view`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => setData({}));
  }, []);

  if (!data) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 flex items-center justify-center">
        <p className="text-slate-400">Loading…</p>
      </div>
    );
  }

  const readiness = data.readiness || {};
  const prefs = data.preferences || {};
  const licStatus = readiness.licence_check_status;
  const statusTone = {
    verified: "bg-emerald-100 text-emerald-800",
    pending: "bg-amber-100 text-amber-800",
    expired: "bg-red-100 text-red-800",
    none: "bg-slate-100 text-slate-600",
  }[licStatus] || "bg-slate-100 text-slate-600";

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-950 pb-20">
      <header className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            data-testid="transfer-back"
            onClick={() => goBack()}
            className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-extrabold text-slate-900 dark:text-white flex items-center gap-1.5">
              <FileText className="w-4 h-4" /> Transfer Ready View
            </h1>
            <p className="text-[11px] text-slate-500">What a new instructor needs to know in 2 minutes</p>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-4 space-y-3">
        {/* Headline */}
        <div data-testid="transfer-headline" className="rounded-2xl bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/15 dark:to-blue-900/15 border border-indigo-200 dark:border-indigo-900/40 p-4">
          <p className="text-xs font-extrabold uppercase tracking-wider text-indigo-800 dark:text-indigo-200">Learner</p>
          <h2 className="text-xl font-extrabold text-slate-900 dark:text-white mt-1">{data.name || "—"}</h2>
          <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
            {data.postcode && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                <MapPin className="w-3 h-3" /> {data.postcode}
              </span>
            )}
            {data.transmission && data.transmission !== "either" && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 capitalize">
                <Car className="w-3 h-3" /> {data.transmission}
              </span>
            )}
          </div>
          {data.headline && data.headline.length > 0 && (
            <ul className="mt-3 text-sm text-indigo-900 dark:text-indigo-100 space-y-1">
              {data.headline.map((h) => (
                <li key={h} className="flex items-start gap-2">
                  <Sparkles className="w-3.5 h-3.5 mt-1 text-indigo-600 dark:text-indigo-300" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Readiness */}
        <div className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4 space-y-2">
          <p className="text-xs font-extrabold uppercase tracking-wider text-slate-500 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Readiness</p>
          <div className="flex items-center gap-2 text-sm">
            <span className={`text-[10px] font-extrabold uppercase px-1.5 py-0.5 rounded ${statusTone}`}>
              Licence · {licStatus || "none"}
            </span>
            {readiness.theory_passed && (
              <span className="text-[10px] font-extrabold uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">Theory passed</span>
            )}
            {readiness.test_booked_date && (
              <span className="inline-flex items-center gap-1 text-[11px] text-slate-700 dark:text-slate-300 ml-auto">
                <Calendar className="w-3 h-3" /> {readiness.test_booked_date}
                {readiness.test_centre ? ` · ${readiness.test_centre}` : ""}
              </span>
            )}
          </div>
        </div>

        {/* Strengths + gaps */}
        <div className="grid grid-cols-2 gap-3">
          <div data-testid="transfer-strengths" className="rounded-2xl bg-emerald-50 dark:bg-emerald-900/15 border border-emerald-200 dark:border-emerald-900/40 p-3">
            <p className="text-[11px] font-extrabold uppercase tracking-wider text-emerald-800 dark:text-emerald-200 mb-1">Strengths</p>
            {(data.strengths || []).length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {data.strengths.map((s) => (
                  <span key={s} className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300">{s}</span>
                ))}
              </div>
            ) : <p className="text-[11px] text-emerald-700/80">Building up</p>}
          </div>
          <div data-testid="transfer-gaps" className="rounded-2xl bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-900/40 p-3">
            <p className="text-[11px] font-extrabold uppercase tracking-wider text-amber-800 dark:text-amber-200 mb-1">Priority gaps</p>
            {(data.priority_gaps || []).length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {data.priority_gaps.map((s) => (
                  <span key={s} className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300">{s}</span>
                ))}
              </div>
            ) : <p className="text-[11px] text-amber-700/80">None right now</p>}
          </div>
        </div>

        {/* Preferences */}
        <div className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4 space-y-2">
          <p className="text-xs font-extrabold uppercase tracking-wider text-slate-500 flex items-center gap-1"><Heart className="w-3.5 h-3.5" /> Teaching style & triggers</p>
          {prefs.teaching_style && (
            <p className="text-sm text-slate-700 dark:text-slate-200">
              Prefers <span className="font-bold capitalize">{prefs.teaching_style}</span> coaching.
            </p>
          )}
          {(prefs.nervous_triggers || []).length > 0 && (
            <div className="flex flex-wrap gap-1">
              {prefs.nervous_triggers.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-rose-100 dark:bg-rose-900/30 text-rose-800 dark:text-rose-300">
                  <AlertTriangle className="w-3 h-3" /> {t}
                </span>
              ))}
            </div>
          )}
          {prefs.reasonable_adjustments && (
            <div className="rounded-lg bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 p-2 text-xs text-slate-700 dark:text-slate-300">
              <span className="font-bold">Reasonable adjustments:</span> {prefs.reasonable_adjustments}
            </div>
          )}
          {(prefs.preferred_days || []).length > 0 && (
            <p className="text-[11px] text-slate-500">
              <span className="font-bold">Available:</span> {prefs.preferred_days.join(" · ")}
              {prefs.preferred_times && prefs.preferred_times.length ? ` · ${prefs.preferred_times.join(", ")}` : ""}
            </p>
          )}
          {prefs.pickup_notes && (
            <p className="text-[11px] text-slate-500">
              <span className="font-bold">Pickup:</span> {prefs.pickup_notes}
            </p>
          )}
        </div>

        {/* Latest handover note */}
        {data.latest_handover_note && (
          <div data-testid="transfer-handover" className="rounded-2xl bg-purple-50 dark:bg-purple-900/15 border border-purple-200 dark:border-purple-900/40 p-4">
            <p className="text-xs font-extrabold uppercase tracking-wider text-purple-800 dark:text-purple-200 mb-1">Latest handover note</p>
            <p className="text-sm text-slate-700 dark:text-slate-200">{data.latest_handover_note.text}</p>
            <p className="mt-2 text-[10px] text-purple-600 dark:text-purple-300">
              — {data.latest_handover_note.instructor_name || "Previous instructor"} · {new Date(data.latest_handover_note.created_at).toLocaleDateString()}
            </p>
          </div>
        )}

        <p className="text-center text-[10px] text-slate-400 py-3">
          Same structure for every learner · Privacy-controlled · Share with consent
        </p>
      </div>
    </div>
  );
}
